import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import { Observable, defer, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { KeycloakConfig, KeycloakProfile } from './keycloak.models';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  private readonly http = inject(HttpClient);
  private keycloak: Keycloak | null = null;
  private config: KeycloakConfig | null = null;

  // Signals for reactive state
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _profile = signal<KeycloakProfile | null>(null);
  private readonly _token = signal<string | undefined>(undefined);

  // Public computed signals
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly profile = this._profile.asReadonly();
  readonly token = this._token.asReadonly();

  constructor() {
    // Auto-refresh token before expiry
    effect(() => {
      if (this._isAuthenticated() && this.keycloak?.token) {
        this.scheduleTokenRefresh();
      }
    });
  }

  init(
    config: KeycloakConfig,
    initOptions: KeycloakInitOptions
  ): Observable<boolean> {
    this.config = config;
    this.keycloak = new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    });

    return defer(() => {
      if (!this.keycloak) {
        throw new Error('Keycloak instance not initialized');
      }
      return this.keycloak.init(initOptions);
    }).pipe(
      tap((authenticated) => {
        this._isAuthenticated.set(authenticated);
        if (authenticated && this.keycloak) {
          this._token.set(this.keycloak.token);
          this.loadUserProfile();
        }
      }),
      catchError((error) => {
        console.error('Keycloak init failed', error);
        return of(false);
      })
    );
  }

  login(): Observable<void> {
    if (!this.keycloak) {
      throw new Error('Keycloak instance not initialized');
    }
    return from(this.keycloak.login()).pipe(
      tap(() => {
        if (this.keycloak) {
          this._isAuthenticated.set(true);
          this._token.set(this.keycloak.token);
          this.loadUserProfile();
        }
      })
    );
  }

  logout(): Observable<void> {
    return defer(() => {
      if (!this.keycloak) {
        throw new Error('Keycloak instance not initialized');
      }
      return this.keycloak.logout({
        redirectUri: window.location.origin,
      });
    }).pipe(
      tap(() => {
        this._isAuthenticated.set(false);
        this._profile.set(null);
        this._token.set(undefined);
      })
    );
  }

  getProfile(): Observable<KeycloakProfile> {
    if (!this._isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    return defer(() => {
      if (!this.keycloak) {
        throw new Error('Keycloak instance not initialized');
      }
      return this.keycloak.loadUserProfile();
    }).pipe(
      map((keycloakProfile) => ({
        id: keycloakProfile.id,
        username: keycloakProfile.username,
        email: keycloakProfile.email,
        firstName: keycloakProfile.firstName,
        lastName: keycloakProfile.lastName,
        attributes: keycloakProfile.attributes,
      })),
      tap((profile) => this._profile.set(profile))
    );
  }

  updateProfile(customSettings: Record<string, unknown>): Observable<void> {
    if (!this._isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }
    if (!this.config) {
      throw new Error('Keycloak config not set');
    }
    if (!this.keycloak) {
      throw new Error('Keycloak instance not initialized');
    }
    const url = `${this.config.url}/realms/${this.config.realm}/account`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.keycloak.token}`,
      'Content-Type': 'application/json',
    });

    return this.http.get(url, { headers }).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      switchMap((currentProfile: any) => {
        const updatedProfile = {
          ...currentProfile,
          attributes: {
            ...currentProfile.attributes,
            ...customSettings,
          },
        };
        return this.http.post<void>(url, updatedProfile, { headers });
      }),
      tap(() => this.loadUserProfile()),
      catchError((error) => {
        console.error('Failed to update profile', error);
        return throwError(() => error);
      })
    );
  }

  private loadUserProfile(): void {
    this.getProfile().subscribe({
      next: (profile) => this._profile.set(profile),
      error: (err) => console.error('Failed to load profile', err),
    });
  }

  private scheduleTokenRefresh(): void {
    if (!this.keycloak?.tokenParsed?.exp) return;

    const expiresIn = this.keycloak.tokenParsed.exp * 1000 - Date.now();
    const refreshTime = Math.max(expiresIn - 60000, 0); // Refresh 1 minute before expiry

    setTimeout(() => {
      if (this._isAuthenticated()) {
        this.refreshToken();
      }
    }, refreshTime);
  }

  private refreshToken(): void {
    if (!this.keycloak) return;
    this.keycloak
      .updateToken(60)
      .then((refreshed) => {
        if (refreshed && this.keycloak) {
          this._token.set(this.keycloak.token);
          this.scheduleTokenRefresh();
        }
      })
      .catch(() => {
        console.error('Failed to refresh token');
        this.logout().subscribe();
      });
  }
}
